/**
 * FilterBar — global filters at the top of every Insights tab.
 *
 * Sticky at the top so it persists across tabs and scroll.
 * Holds: time range preset, department filter, agent filter, model filter.
 *
 * The FilterBar itself is a controlled component (`filters` + `onChange`)
 * so it stays reusable in non-URL-bound contexts. URL binding lives in
 * the `useInsightsSearchFilters` hook (see
 * `use-insights-search-filters.ts`) which Insights routes use as a
 * drop-in replacement for `useState<InsightsFilters>(...)`. The hook
 * reads/writes URL search params via TanStack Router's `useSearch()`
 * + `useNavigate()`, so filters survive tab switches and page refresh,
 * and the resulting URLs are deep-linkable.
 *
 * Per resolved decision #9 (founder-locked) — see
 * docs/testing/PHASE2_RESOLVED_DECISIONS_2026-05-14.md and
 * INS-FILTER-PERSIST-001.
 */

import { Calendar, Building2, Bot, Cpu, Filter as FilterIcon, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type TimeRange = '24h' | '7d' | '30d' | '90d' | 'custom';

export interface InsightsFilters {
  range: TimeRange;
  department: string;       // 'all' | dept name
  agentId: string;          // 'all' | agent id
  modelId: string;          // 'all' | model id
}

export const DEFAULT_FILTERS: InsightsFilters = {
  range: '7d',
  department: 'all',
  agentId: 'all',
  modelId: 'all',
};

/**
 * R3-P0 defect #1 — convert UI filters to the wire-format filter shape
 * accepted by every /insights-module/* endpoint.
 *
 * Pod-KK #613 — `custom` range still falls back to 7d (the backend
 * doesn't accept a date-pair yet) but the fallback is now flagged
 * EXPLICITLY via `customFallback: true`. Routes use that flag to render
 * a "Custom range not yet supported — showing 7d" banner instead of
 * silently lying to the user.
 */
export function filtersToParams(f: InsightsFilters): {
  range: '24h' | '7d' | '30d' | '90d';
  department: string;
  agentId: string;
  modelId: string;
  customFallback: boolean;
} {
  const isCustom = f.range === 'custom';
  const wireRange: '24h' | '7d' | '30d' | '90d' =
    isCustom ? '7d' : (f.range as '24h' | '7d' | '30d' | '90d');
  return {
    range: wireRange,
    department: f.department,
    agentId: f.agentId,
    modelId: f.modelId,
    customFallback: isCustom,
  };
}

/**
 * Pod-KK #111 — human-readable suffix for KPI cards and chart titles
 * (e.g. "Spend (30d)"). The previous version had every label hardcoded
 * to "(30d)" regardless of the selected range, which contradicted the
 * underlying data. `custom` falls back to "7d" (mirrors filtersToParams).
 */
export function rangeLabel(r: TimeRange): string {
  switch (r) {
    case '24h': return '24h';
    case '7d':  return '7d';
    case '30d': return '30d';
    case '90d': return '90d';
    case 'custom': return '7d';
  }
}

/**
 * Bug #1117 — full-sentence range phrase for empty-state copy
 * ("No cost data in the last 7 days." vs the prior hardcoded
 * "last 30 days" which contradicted the visible 7d/24h selection).
 * Uses the same custom-range fallback as rangeLabel().
 */
export function rangeEmptyStatePhrase(r: TimeRange): string {
  switch (r) {
    case '24h':    return 'in the last 24 hours';
    case '7d':     return 'in the last 7 days';
    case '30d':    return 'in the last 30 days';
    case '90d':    return 'in the last 90 days';
    case 'custom': return 'for the selected range';
  }
}

export interface FilterBarProps {
  filters: InsightsFilters;
  onChange: (filters: InsightsFilters) => void;
  /** Action items count for the right-side button */
  actionItemsCount?: number;
  onOpenActionItems?: () => void;
  className?: string;
}

// Bug #1116 — live tenant filter options replace hardcoded arrays.
//
// Pre-fix the visible option list embedded fictional ids
// (`agent-ask-argus`, `qwen-72b-awq`, the 12 retired industrial agents)
// which did NOT match the canonical slugs the API normalizes to
// (`system.ask_argus`, `argus-fast`). Selecting any visible filter
// zeroed out the Insights data because the WHERE clause matched nothing.
//
// The new `/insights-module/filter-options` endpoint returns live
// agent + model lists by scanning the tenant's last-30d query_metrics
// and normalising the slugs the SAME WAY the metric routes do. The
// FilterBar now renders that list verbatim and passes the exact id
// back to the metric endpoints — perfect parity, no hidden mapping.
//
// The legacy DEPARTMENTS sample list is kept ONLY as a fallback for
// tenants whose `departments` table is empty (e.g. fresh-deploy QA
// environments). Once the endpoint returns at least one department,
// the live list replaces it.
const FALLBACK_DEPARTMENTS = [
  'Operations', 'Maintenance', 'Engineering', 'Compliance & Legal',
  'Safety', 'Procurement', 'IT & Security', 'Executive',
];

interface FilterOption {
  id: string;
  label: string;
}

interface FilterOptionsResponse {
  agents: FilterOption[];
  models: FilterOption[];
  departments: FilterOption[];
}

function useFilterOptions() {
  return useQuery<FilterOptionsResponse>({
    queryKey: ['insights-module', 'filter-options'],
    queryFn: () =>
      apiClient.get<FilterOptionsResponse>('/insights-module/filter-options'),
    // The option set rarely changes — keep it warm so toggling filters
    // doesn't re-fetch on every page change. 5 min is enough to capture
    // a newly-onboarded agent appearing in usage.
    staleTime: 5 * 60_000,
  });
}

export function FilterBar({
  filters, onChange, actionItemsCount = 0, onOpenActionItems, className,
}: FilterBarProps) {
  const { data: liveOptions } = useFilterOptions();
  const agents = liveOptions?.agents ?? [];
  const models = liveOptions?.models ?? [];
  const departmentOptions: string[] =
    liveOptions?.departments && liveOptions.departments.length > 0
      ? liveOptions.departments.map((d) => d.label)
      : FALLBACK_DEPARTMENTS;

  const isFiltered = filters.department !== 'all' || filters.agentId !== 'all' || filters.modelId !== 'all';

  return (
    <div className={cn(
      'sticky top-0 z-10 flex items-center gap-2 px-4 py-2 bg-background/95 backdrop-blur-sm border-b',
      className,
    )}>
      {/* Time range */}
      <Select value={filters.range} onValueChange={(v) => onChange({ ...filters, range: v as TimeRange })}>
        <SelectTrigger aria-label="Filter Insights by time range" className="h-8 w-32 text-xs gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="24h" className="text-xs">Last 24h</SelectItem>
          <SelectItem value="7d"  className="text-xs">Last 7 days</SelectItem>
          <SelectItem value="30d" className="text-xs">Last 30 days</SelectItem>
          <SelectItem value="90d" className="text-xs">Last 90 days</SelectItem>
          <SelectItem value="custom" className="text-xs">Custom range…</SelectItem>
        </SelectContent>
      </Select>

      {/* Department */}
      <Select value={filters.department} onValueChange={(v) => onChange({ ...filters, department: v })}>
        <SelectTrigger aria-label="Filter Insights by department" className="h-8 w-44 text-xs gap-1.5">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <SelectValue placeholder="All departments" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">All departments</SelectItem>
          {departmentOptions.map(d => (
            <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Agent — live tenant list from /insights-module/filter-options.
          Bug #1116: each option's id is the EXACT canonical slug used
          by the metric routes, so the same id passes through every
          filter-aware endpoint without translation. */}
      <Select value={filters.agentId} onValueChange={(v) => onChange({ ...filters, agentId: v })}>
        <SelectTrigger aria-label="Filter Insights by agent" className="h-8 w-48 text-xs gap-1.5">
          <Bot className="h-3.5 w-3.5 text-muted-foreground" />
          <SelectValue placeholder="All agents" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">All agents</SelectItem>
          {agents.length === 0 ? (
            <SelectItem value="__no_data__" disabled className="text-xs italic text-muted-foreground">
              No agent activity yet
            </SelectItem>
          ) : (
            agents.map(a => (
              <SelectItem key={a.id} value={a.id} className="text-xs">{a.label}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {/* Model — live tenant list, same parity guarantee as agents. */}
      <Select value={filters.modelId} onValueChange={(v) => onChange({ ...filters, modelId: v })}>
        <SelectTrigger aria-label="Filter Insights by model" className="h-8 w-40 text-xs gap-1.5">
          <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
          <SelectValue placeholder="All models" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-xs">All models</SelectItem>
          {models.length === 0 ? (
            <SelectItem value="__no_data__" disabled className="text-xs italic text-muted-foreground">
              No model usage yet
            </SelectItem>
          ) : (
            models.map(m => (
              <SelectItem key={m.id} value={m.id} className="text-xs">{m.label}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {/* Reset */}
      {isFiltered && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1"
          onClick={() => onChange({ ...DEFAULT_FILTERS, range: filters.range })}
        >
          <X className="h-3 w-3" /> Reset
        </Button>
      )}

      <div className="flex-1" />

      {/* Action Items button */}
      {onOpenActionItems && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={onOpenActionItems}
        >
          <FilterIcon className="h-3.5 w-3.5" />
          Action Items
          {actionItemsCount > 0 && (
            <Badge variant="destructive" className="h-4 px-1.5 text-[9px] font-bold tabular-nums">
              {actionItemsCount}
            </Badge>
          )}
        </Button>
      )}
    </div>
  );
}
